import axios from 'axios';

// Create an axios instance with the correct base URL
const api = axios.create({
  baseURL: 'https://exam.doode.in', // Use 'http://localhost:5000' for local testing
  timeout: 10000,
  withCredentials: true // Important for CORS with credentials
});

// Helper function to get the appropriate token based on current user type
const getAuthToken = () => {
  // Determine which dashboard/context we're in by checking the current URL or user context
  const currentPath = window.location.pathname;
  
  // If we're on SuperAdmin routes, prioritize SuperAdmin token
  if (currentPath.startsWith('/dashboard') || currentPath.includes('superadmin')) {
    const superAdminToken = localStorage.getItem('adminToken');
    const superAdminRole = localStorage.getItem('role');
    
    if (superAdminToken && superAdminRole === 'superadmin') {
      return superAdminToken;
    }
  }
  
  // If we're on Admin routes, prioritize Admin token  
  if (currentPath.startsWith('/admin/') || currentPath.includes('admin')) {
    const collegeAdminToken = localStorage.getItem('collegeAdminToken');
    const collegeAdminRole = localStorage.getItem('collegeAdminRole');
    
    if (collegeAdminToken && collegeAdminRole === 'admin') {
      return collegeAdminToken;
    }
  }
  
  // Fallback logic - check what's available
  // First check for super admin
  const superAdminToken = localStorage.getItem('adminToken');
  const superAdminRole = localStorage.getItem('role');
  
  if (superAdminToken && superAdminRole === 'superadmin') {
    return superAdminToken;
  }
  
  // Then check for college admin
  const collegeAdminToken = localStorage.getItem('collegeAdminToken');
  const collegeAdminRole = localStorage.getItem('collegeAdminRole');
  
  if (collegeAdminToken && collegeAdminRole === 'admin') {
    return collegeAdminToken;
  }
  
  // Final fallback
  return localStorage.getItem('adminToken') || localStorage.getItem('token');
};

// Add request interceptor with improved logging and token handling
api.interceptors.request.use(
  config => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log(`Making ${config.method.toUpperCase()} request to ${config.baseURL}${config.url}`);
    try {
      const fullUrl = new URL(config.url, config.baseURL).toString();
      console.log(`Full request URL: ${fullUrl}`);
    } catch (e) {
      console.log(`Could not construct full URL: ${e.message}`);
    }
    return config;
  },
  error => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for better error handling
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response) {
      console.error('Server error:', error.response.status, error.response.data);
    } else if (error.request) {
      console.error('Network error - no response received');
      console.error('Request details:', JSON.stringify({
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        method: error.config?.method
      }));
    } else {
      console.error('Request configuration error:', error.message);
    }
    return Promise.reject(error);
  }
);

// SuperAdmin API calls
export const superAdminAPI = {
  login: (credentials) => {
    console.log('Logging in SuperAdmin:', credentials.username);
    return api.post('/api/superAdminRoutes/superadminlogin', credentials);
  },
};

// Admin API calls
export const adminAPI = {
  login: (credentials) => api.post('/api/adminlogin', credentials),
  getAll: () => api.get('/api/admins'),
  register: (data) => api.post('/api/admins/register', data),
  getColleges: async () => {
    const token = getAuthToken();
    const response = await fetch('/api/admins/colleges', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
    });
    if (!response.ok) throw new Error('Failed to fetch colleges');
    return response.json();
  },
};

// Question API calls
export const questionAPI = {
  getAll: () => api.get('/api/questions'),
  getById: (id) => api.get(`/api/questions/${id}`),
  create: (data) => api.post('/api/questions', data),
  update: (id, data) => api.put(`/api/questions/${id}`, data),
  delete: (id) => api.delete(`/api/questions/${id}`),
  createBulk: (data) => api.post('/api/questions/bulk', data)
};

// Student API calls
export const studentAPI = {
  getAll: () => api.get('/api/student'),
  getById: (id) => api.get(`/api/student/${id}`),
  create: (data) => api.post('/api/student', data),
  register: (studentData) => api.post('/api/student/admin/register', studentData),
  portalRegister: (studentData) => api.post('/api/student/register', studentData),
  update: (id, data) => api.put(`/api/student/${id}`, data),
  delete: (id) => api.delete(`/api/student/${id}`),
  createBulk: (data) => api.post('/api/student/admin/bulk', data),
  // Fixed getCompletedTests method with correct route and enhanced logging
  getCompletedTests: (collegeName = null) => {
    const url = '/api/results/completed-tests';
    console.log('getCompletedTests called with collegeName:', collegeName);
    
    if (collegeName) {
      console.log('Making request with collegeName parameter:', collegeName);
      return api.get(url, { params: { collegeName } });
    } else {
      console.log('Making request without collegeName (superadmin)');
      return api.get(url);
    }
  },
  stats: {
    getTotal: (collegeName = null) => {
      const url = '/api/student-stats/total';
      return collegeName
        ? api.get(url, { params: { collegeName } })
        : api.get(url);
    },
    getTestTakers: (collegeName = null) => {
      const url = '/api/student-stats/test-takers';
      return collegeName
        ? api.get(url, { params: { collegeName } })
        : api.get(url);
    },
    getTotalPayments: () => api.get('/api/student-stats/total-payments'),
  }
};

// Assignment API calls
export const assignmentAPI = {
  getAll: () => api.get('/api/assignments'),
  getById: (id) => api.get(`/api/assignments/${id}`),
  create: (data) => api.post('/api/assignments', data),
  createBatch: (data) => api.post('/api/assignments/batch', data),
  complete: (id, data) => api.put(`/api/assignments/${id}/complete`, data),
  getByStudent: (studentId) => api.get(`/api/assignments/student/${studentId}`)
};

// Health check
export const healthCheck = () => api.get('/api/health')
  .then(response => ({
    status: 'connected',
    details: response.data,
    baseUrl: api.defaults.baseURL
  }))
  .catch(error => ({
    status: 'error',
    message: error.message,
    baseUrl: api.defaults.baseURL
  }));

// Payment API calls
export const paymentAPI = {
  checkPaymentRequirement: (studentId) => api.get(`/api/payments/check-requirement/${studentId}`),
  createOrder: (data) => api.post('/api/create-order', data),
  verifyAndGenerateTest: (data) => api.post('/api/verify-payment', data),
  getPaymentHistory: (studentId) => api.get(`/api/payments/history/${studentId}`),
  getPaymentStatus: (studentId) => api.get(`/api/payments/status/${studentId}`),
};

export default api;